/**
 * Shared design RUNTIME (Run-mode action/effect engine).
 *
 * One canonical implementation of the client-side runtime that used to be
 * triplicated (and diverged) across the three "Run" surfaces. It covers:
 *
 *   - findNode        recursive node lookup (any depth — groups, baked
 *                     component children, carousel slides)
 *   - runAction       the event-action interpreter (navigate / toggle /
 *                     set_variant / goto_state / toggle_state / command /
 *                     set_variable), incl. {row.field} interpolation from a
 *                     marker-click context
 *   - onTrigger       click / dblclick / markerClick → the node's actions
 *   - onControl       slider/toggle/input change → memory var or write-back
 *   - onPluginSetVar / onPluginCommand   widget-SDK plugin writes
 *   - tick / scheduleTick / startPolling   design_render → ShapeEffects
 *     (incl. animFor: the active animation spec per shape)
 *   - liveEffects     render effects merged with runtime hidden/variant state
 *   - collectBoundVars / applyLiveSample   LiveHub WS push support
 *
 * Intentional per-surface differences are expressed ONLY via options — never
 * by copying this logic:
 *
 *   Design.vue (editor Run mode)
 *     enabled: () => preview        handlers are inert while editing
 *     render: callTool('design_render')   authenticated user session
 *     write: { allowed: commandMode, send: control_write }  PLC write-back,
 *            gated by the Command-mode toggle; notify = toast feedback
 *     navigate: same-tab router.push (or window.open for newTab/external)
 *     coalesceControlTicks: true    slider input debounced into one render
 *     Uses collectBoundVars/applyLiveSample for the LiveHub WebSocket, seeds
 *     localVars from doc variables, resetRuntime/clearEffects on Run toggle.
 *
 *   DesignLiveView.vue (inline chat viewer, MCP App)
 *     render: callTool('design_render')   authenticated user session
 *     write: NOT provided — 'command' actions and writeBack controls are
 *            intentionally ignored from the chat view
 *     navigate: always a new tab (never hijack the host portal)
 *
 *   EmbedDesignView.vue (tokenized kiosk screen)
 *     render: fetch /api/designs/:id/render?token=…   embed token, no
 *             user session / callTool
 *     write: NOT provided — no PLC write-back from a kiosk screen
 *     navigate: always a new tab
 *
 * Behaviors that are uniform ON PURPOSE (recent fixes, now in one place):
 *   - findNode is RECURSIVE in onTrigger/onControl — triggers from nested
 *     nodes (group children, baked component instances, carousel slides,
 *     a map's markerClick) resolve on every surface.
 *   - tick keeps the LAST GOOD effects when design_render returns no
 *     shapes array (transient failure) instead of blanking every shape.
 *   - markerClick rows ({row.field} interpolation) work on every surface,
 *     not just the editor.
 */
import { computed, onBeforeUnmount, reactive, ref, type ComputedRef, type Ref } from 'vue';
import type { ShapeEffect } from './DesignCanvas.vue';
import type { AnimSpec } from './animCss';
import type { DesignerNode } from './opmodel';
import { appConfirm } from '../composables/useAppDialog';
import { applyScalarTransforms } from './scalarTransform';

/** One shape's evaluation from design_render. */
export interface RenderShape {
  id: string;
  value: number | null;
  active_state: number;
  overrides: Record<string, unknown>;
  series?: ShapeEffect['series'];
  table?: ShapeEffect['table'];
  options?: string[];
  repeat?: { var: string; values: string[] };
  // ADR 41 F3 — freshness/route of a connector point binding (for the
  // "updated Xs ago" / "no connection" badge). Absent for warehouse series.
  route?: 'stored' | 'live';
  age_ms?: number;
  quality?: string;
  last_value?: unknown;
  received_at_ms?: number | null;
  live_error?: string;
}

/** An event action as stored in node.events.{click|dblclick|markerClick}. */
export interface ActionDef {
  type: string; target?: string; mode?: string; value?: string; name?: string; scope?: string;
  newTab?: boolean; state?: string; variable_id?: string; ask?: boolean; inputKind?: string;
  promptLabel?: string; min?: number; max?: number; unit?: string;
}

/** SynValuePrompt.open() options (keypad / text entry). */
export interface PromptOptions { kind?: 'text' | 'number'; label?: string; initial?: string; unit?: string; min?: number; max?: number }

export interface DesignRuntimeOptions {
  /** Session renderers also reauthorize static captured screens on each poll. */
  alwaysRender?: boolean;
  generation?: () => number;
  customAction?: (action:ActionDef, gesture:{node_id:string;event:'click'|'dblclick'|'markerClick';index:number}, vars:Record<string,unknown>) => Promise<boolean>;
  /** Top-level shapes of the doc (root.children) — a getter so doc reloads/replacements are always seen. */
  nodes: () => DesignerNode[];
  /** Transport for design_render: authenticated callTool OR tokenized embed fetch. */
  render: (vars: Record<string, unknown>) => Promise<{ shapes?: RenderShape[] }>;
  /** Run-mode gate; handlers are inert when false (editor: `preview`). Default: always on. */
  enabled?: () => boolean;
  /** Value entry UI (SynValuePrompt) for `ask` actions; undefined/cancel aborts the action. */
  prompt?: (opts: PromptOptions) => Promise<string | null> | undefined;
  /** Navigation capability. `internal` gets the parsed target ({kind,id}); the
   *  surface decides the route (editor `/design/:id`, kiosk `/screen/design/:id`,
   *  …). Absent → navigate actions are ignored. */
  navigate?: {
    external: (url: string, newTab: boolean) => void;
    internal: (target: { kind: 'design' | 'dashboard'; id: string }, newTab: boolean) => void;
  };
  /** Process write-back capability (command actions, writeBack controls, plugin commands). Absent → intentionally ignored (read-only surfaces). */
  write?: {
    allowed: () => boolean;
    send: (variableId: string, value: number) => void | Promise<void>;
  };
  /** User feedback (toasts). Absent → silent. */
  notify?: (msg: string) => void;
  /** Debounce control/plugin var changes into one render (editor); default: immediate tick. */
  coalesceControlTicks?: boolean;
}

/**
 * Does any node (recursively) need server-side render (design_render)? Only
 * data-driven elements do: a dataBinding, chart/table series/columns, a
 * data-conditioned state (`when`), or a dynamic prop binding. A design made only
 * of static shapes + plugin widgets (which fetch their own data via the widget
 * SDK) needs NONE — the kiosk/run surfaces use this to skip the render poll.
 */
export function designNeedsRender(list: DesignerNode[]): boolean {
  for (const n of list) {
    if (n.dataBinding) return true;
    const p = (n.props ?? {}) as Record<string, unknown>;
    if (Array.isArray(p.series) && p.series.length) return true;
    if (Array.isArray(p.columns) && p.columns.length) return true;
    if (p.bindings && typeof p.bindings === 'object' && Object.keys(p.bindings as object).length) return true;
    const states = n.states as Array<{ when?: string }> | undefined;
    if (Array.isArray(states) && states.some((s) => s.when)) return true;
    const kids = (n as { children?: DesignerNode[] }).children;
    if (Array.isArray(kids) && designNeedsRender(kids)) return true;
  }
  return false;
}

export function useDesignRuntime(opts: DesignRuntimeOptions): {
  effects: Ref<Record<string, ShapeEffect>>;
  liveEffects: ComputedRef<Record<string, ShapeEffect>>;
  forcedState: Record<string, string>;
  localVars: Record<string, unknown>;
  findNode: (id: string, list?: DesignerNode[]) => DesignerNode | null;
  runAction: (a: ActionDef, ctx?: Record<string, unknown>) => Promise<void>;
  onTrigger: (nodeId: string, event: 'click' | 'dblclick' | 'markerClick', ctx?: Record<string, unknown>) => Promise<void>;
  onControl: (nodeId: string, value: number | string | string[]) => void;
  onPluginSetVar: (name: string, value: unknown) => void;
  onPluginCommand: (id: string, value: unknown) => void;
  tick: () => Promise<void>;
  scheduleTick: () => void;
  cancelScheduledTick: () => void;
  startPolling: (intervalMs: number) => void;
  stopPolling: () => void;
  /** True when any node needs server-side design_render (data widgets, conditioned states, bindings). */
  needsRender: () => boolean;
  collectBoundVars: () => string[];
  applyLiveSample: (s: { variable_id: string; value: number }) => void;
  // ADR 41 F4 — connector point live (ConnectorLiveHub) push support.
  collectBoundPoints: () => Array<{ connector_id: string; selection: Record<string, unknown> }>;
  bindPointKeys: (subscribed: Array<{ connector_id: string; selection: Record<string, unknown>; key: string }>) => void;
  applyPointSample: (s: { key: string; value: number | null; ts: string | null; route: 'live' }) => void;
  resetRuntime: () => void;
  clearEffects: () => void;
} {
  const enabled = opts.enabled ?? (() => true);
  const notify = opts.notify;

  // Per-shape effects from design_render (style overrides + value + anim).
  const effects = ref<Record<string, ShapeEffect>>({});
  // Runtime state set by event actions: visibility toggles, forced variants,
  // forced named states, and local UI variables fed back into condition eval.
  const runtimeHidden = reactive<Record<string, boolean>>({});
  const runtimeVariant = reactive<Record<string, string>>({});
  const forcedState = reactive<Record<string, string>>({}); // goto_state: nodeId → state name
  const localVars = reactive<Record<string, unknown>>({});

  const liveEffects = computed<Record<string, ShapeEffect>>(() => {
    const base: Record<string, ShapeEffect> = { ...effects.value };
    for (const [id, h] of Object.entries(runtimeHidden)) base[id] = { ...(base[id] ?? {}), hidden: h };
    for (const [id, v] of Object.entries(runtimeVariant)) base[id] = { ...(base[id] ?? {}), variant: v };
    return base;
  });

  /** Recursive lookup: triggers/controls may come from a NESTED node (e.g. a
   *  map's markerClick inside a group / component instance), not just top level. */
  function findNode(id: string, list: DesignerNode[] = opts.nodes()): DesignerNode | null {
    for (const n of list) {
      if (n.id === id) return n;
      const kids = (n as { children?: DesignerNode[] }).children;
      if (Array.isArray(kids)) { const hit = findNode(id, kids); if (hit) return hit; }
    }
    return null;
  }

  // Interpolate {row.field} / {field} tokens with a marker row (markerClick).
  function interp(s: string | undefined, ctx?: Record<string, unknown>): string | undefined {
    if (s == null || !ctx) return s;
    return s.replace(/\{(?:row\.)?([A-Za-z0-9_]+)\}/g, (m, k: string) => (k in ctx ? String(ctx[k]) : m));
  }

  async function runAction(a: ActionDef, ctx?: Record<string, unknown>): Promise<void> {
    if (ctx) a = { ...a, target: interp(a.target, ctx), value: interp(a.value, ctx), variable_id: interp(a.variable_id, ctx), name: interp(a.name, ctx) };
    if (a.type === 'navigate' && a.target) {
      if (!opts.navigate) return;
      if (/^https?:\/\//.test(a.target)) { opts.navigate.external(a.target, a.newTab === true); return; }
      const i = a.target.indexOf(':');
      if (i < 0) return;
      const kind = a.target.slice(0, i);
      const id = a.target.slice(i + 1);
      if ((kind !== 'design' && kind !== 'dashboard') || !id) return;
      opts.navigate.internal({ kind, id }, a.newTab === true);
    } else if (a.type === 'toggle' && a.target) {
      const cur = runtimeHidden[a.target] ?? false;
      runtimeHidden[a.target] = a.mode === 'show' ? false : a.mode === 'hide' ? true : !cur;
    } else if (a.type === 'set_variant' && a.target && a.value != null) {
      runtimeVariant[a.target] = a.value;
    } else if (a.type === 'goto_state' && a.target) {
      // Force a named state on the target (empty state name clears it).
      if (a.state) forcedState[a.target] = a.state; else delete forcedState[a.target];
    } else if (a.type === 'toggle_state' && a.target && a.state) {
      // Flip a named state on the target on/off: force it, or release it back to
      // the base/data-driven style if it's already the forced one. Lets a button
      // toggle another shape's colour (base fill ⇄ the state's fill).
      if (forcedState[a.target] === a.state) delete forcedState[a.target];
      else forcedState[a.target] = a.state;
    } else if (a.type === 'command' && a.variable_id) {
      // Write-back: only on surfaces with the write capability (the chat/kiosk
      // views intentionally ignore it); server enforces RBAC.
      if (!opts.write) return;
      if (!opts.write.allowed()) { notify?.('Enable "Command mode" to write variables.'); return; }
      let val: number;
      if (a.ask) {
        // The keypad (with min/max/unit) IS the value entry + safety confirm.
        const entered = await opts.prompt?.({ kind: 'number', label: a.promptLabel || `Write ${a.variable_id}`, initial: a.value, min: a.min, max: a.max, unit: a.unit });
        if (entered == null) return; // cancelled
        val = Number(entered);
      } else {
        val = Number(a.value);
        if (!Number.isFinite(val)) { notify?.('Invalid command value.'); return; }
        if (!(await appConfirm(`Write to process:\n\n${a.variable_id} = ${val}\n\nConfirm the command?`))) return;
      }
      if (!Number.isFinite(val)) { notify?.('Invalid command value.'); return; }
      void opts.write.send(a.variable_id, val);
    } else if (a.type === 'set_variable' && a.name) {
      // "Ask the user": prompt with a keypad / text field, else use the fixed value.
      let raw = a.value ?? '';
      if (a.ask) {
        const entered = await opts.prompt?.({ kind: a.inputKind === 'text' ? 'text' : 'number', label: a.promptLabel || `Value for ${a.name}`, initial: a.value, min: a.min, max: a.max, unit: a.unit });
        if (entered == null) return; // cancelled
        raw = entered;
      }
      // Numbers stay numeric so conditions like "$modo > 0" work.
      const num = Number(raw);
      localVars[a.name] = raw !== '' && !Number.isNaN(num) ? num : raw;
      void tick(); // re-evaluate conditions with the new variable
    }
  }

  async function onTrigger(nodeId: string, event: 'click' | 'dblclick' | 'markerClick', ctx?: Record<string, unknown>): Promise<void> {
    if (!enabled()) return;
    const node = findNode(nodeId);
    const actions = ((node?.events as Record<string, ActionDef[]> | undefined)?.[event]) ?? [];
    const generation = opts.generation?.();
    for (const [index,a] of actions.entries()) {
      if (!enabled() || generation !== opts.generation?.()) break;
      if (a.type.startsWith('project_')) {
        if (!opts.customAction || !await opts.customAction(a,{node_id:nodeId,event,index},localVars)) break;
      } else await runAction(a,ctx);
    }
  }

  // An input control (slider/toggle/input) changed: write its bound variable.
  // `writeBack` props → process command (write capability, command-mode gated);
  // otherwise a memory variable that drives the live view (set_variable).
  function onControl(nodeId: string, value: number | string | string[]): void {
    if (!enabled()) return;
    const node = findNode(nodeId);
    if (!node) return;
    const p = (node.props ?? {}) as Record<string, unknown>;
    const binding = node.dataBinding as { series_id?: string; variable_id?: string } | undefined;
    const varId = (typeof p.varName === 'string' && p.varName) || binding?.series_id || binding?.variable_id;
    if (!varId) { notify?.('This control has no variable bound.'); return; }
    if (p.writeBack) {
      if (!opts.write) return; // read-only surface: no PLC write-back
      if (!opts.write.allowed()) { notify?.('Enable "Command mode" to write variables.'); return; }
      const num = Number(value);
      if (!Number.isFinite(num)) { notify?.('Control value is not numeric.'); return; }
      void opts.write.send(varId, num);
    } else {
      localVars[varId] = value;
      // Editor coalesces rapid slider input into one render (debounced).
      if (opts.coalesceControlTicks) scheduleTick(); else void tick();
    }
  }

  // A hosted plugin bundle asked to write (widget-SDK setVariable/command).
  // setVariable → a runtime memory variable; command → process write-back,
  // gated by the write capability (same policy as the `command` event action).
  function onPluginSetVar(name: string, value: unknown): void {
    if (!enabled() || !name) return;
    localVars[name] = value;
    if (opts.coalesceControlTicks) scheduleTick(); else void tick();
  }
  function onPluginCommand(id: string, value: unknown): void {
    if (!enabled() || !id) return;
    if (!opts.write) return; // read-only surface: no PLC write-back
    if (!opts.write.allowed()) { notify?.('Enable "Command mode" to let a plugin write to the process.'); return; }
    const num = Number(value);
    if (!Number.isFinite(num)) { notify?.('Plugin command value is not numeric.'); return; }
    void opts.write.send(id, num);
  }

  /** The active animation spec for a shape (run mode). */
  function animFor(node: DesignerNode | undefined, active: boolean): AnimSpec | undefined {
    for (const a of (node?.animations as AnimSpec[] | undefined) ?? []) {
      if (!a.kind) continue;
      if (a.when === 'active' && !active) continue;
      return a;
    }
    return undefined;
  }

  // Skip server-side render when no node needs it (see designNeedsRender).
  // Re-evaluated whenever the node-list reference changes (doc reload).
  let needsRenderNodesRef: DesignerNode[] | null = null;
  let needsRenderCached = true;
  function needsRender(): boolean {
    const ns = opts.nodes();
    if (ns !== needsRenderNodesRef) {
      needsRenderNodesRef = ns;
      needsRenderCached = designNeedsRender(ns);
    }
    return needsRenderCached;
  }

  // Poll design_render and translate the per-shape state evaluation into
  // canvas effects (style overrides + live value + active animation). Runtime
  // vars set by actions/controls are passed so data conditions re-evaluate.
  async function tick(): Promise<void> {
    if (!opts.alwaysRender && !needsRender()) return; // nothing server-resolved → don't hit the network
    try {
      const r = await opts.render({ ...localVars });
      // If the render errored (no shapes array), keep the last good effects rather
      // than blanking every shape on a transient failure.
      if (!Array.isArray(r.shapes)) return;
      const byId = new Map(opts.nodes().map((n) => [n.id, n] as const));
      const next: Record<string, ShapeEffect> = {};
      for (const s of r.shapes) {
        const ov = s.overrides ?? {};
        // Spread ALL overrides so dynamic property bindings (opacity/fontSize/
        // radius/…) flow through, not just the fixed style set.
        next[s.id] = {
          ...(ov as Partial<ShapeEffect>),
          value: s.value,
          series: s.series,
          table: s.table,
          options: s.options,
          repeat: s.repeat,
          animSpec: animFor(byId.get(s.id), s.active_state >= 0),
          // ADR 41 F3 — carry point-binding freshness to the canvas badge.
          route: s.route,
          age_ms: s.age_ms,
          quality: s.quality,
          last_value: s.last_value,
          received_at_ms: s.received_at_ms,
          live_error: s.live_error,
        };
      }
      // ADR 41 F4 — a fresher hub push (point_sample) wins over this poll's
      // render-resolved point value, so a slow poll can't show a staler reading.
      for (const [id, ov] of livePointOverrides) {
        if (next[id]) next[id] = { ...next[id], value: ov.value, route: ov.route, age_ms: ov.ageMs ?? undefined };
      }
      effects.value = next;
    } catch { /* runtime poll is best-effort */ }
  }

  // Debounced tick (coalesces bursts: live samples, slider input).
  let tickPending: ReturnType<typeof setTimeout> | null = null;
  function scheduleTick(): void {
    if (tickPending) return;
    tickPending = setTimeout(() => { tickPending = null; void tick(); }, 250);
  }
  function cancelScheduledTick(): void {
    if (tickPending) { clearTimeout(tickPending); tickPending = null; }
  }

  // Render polling (the safety net / fallback alongside WS push).
  let timer: ReturnType<typeof setInterval> | null = null;
  function startPolling(intervalMs: number): void {
    stopPolling();
    timer = setInterval(() => { void tick(); }, intervalMs);
  }
  function stopPolling(): void {
    if (timer) { clearInterval(timer); timer = null; }
  }

  // ── Live push (LiveHub WS) support ──────────────────────────────────────
  /** Bound variable_ids in the doc; also (re)builds the variable→node index. */
  let varToNodes = new Map<string, string[]>();
  function collectBoundVars(): string[] {
    const map = new Map<string, string[]>();
    const walk = (n: DesignerNode): void => {
      // A single-series binding (series_id) gets live sample updates; legacy
      // 'variable' bindings read the same way (series_id = the old variable_id).
      const b = n.dataBinding as { kind?: string; series_id?: string; variable_id?: string; agg?: string; transforms?: unknown[] } | undefined;
      const sid = b ? (b.series_id ?? (b.kind === 'variable' ? b.variable_id : undefined)) : undefined;
      // Only push the latest raw sample when the element shows the LATEST value
      // (agg unset/'last') AND has no transform pipeline (ADR 44). With an
      // aggregation (count/avg/sum/…) OR a transform, the displayed value is
      // computed by design_render — a single raw live sample would wrongly
      // override it (e.g. a KPI with `calc {value}+100` showing the raw reading).
      if (sid && (b!.agg ?? 'last') === 'last' && !(Array.isArray(b!.transforms) && b!.transforms.length)) {
        const arr = map.get(sid) ?? [];
        arr.push(n.id);
        map.set(sid, arr);
      }
      for (const c of n.children ?? []) walk(c);
    };
    for (const n of opts.nodes()) walk(n);
    varToNodes = map;
    return [...map.keys()];
  }
  /** A pushed sample: update bound scalars instantly; the full state/series
   *  refresh is debounced. */
  function applyLiveSample(s: { variable_id: string; value: number }): void {
    const ids = varToNodes.get(s.variable_id);
    if (ids?.length) {
      const next = { ...effects.value };
      for (const id of ids) next[id] = { ...(next[id] ?? {}), value: s.value };
      effects.value = next;
    }
    scheduleTick();
  }

  // ── Connector point live push (ConnectorLiveHub) — ADR 41 F4 ────────────
  // Local key for a point binding (connector + sorted selection); the backend
  // assigns its own (tenant-scoped) watch key, mapped to ours via bindPointKeys.
  const localPointKey = (connectorId: string, selection: Record<string, unknown>): string => {
    const sorted = Object.keys(selection).sort().reduce<Record<string, unknown>>((o, k) => { o[k] = selection[k]; return o; }, {});
    return `${connectorId} ${JSON.stringify(sorted)}`;
  };
  let localPointToNodes = new Map<string, string[]>(); // localKey → node ids
  let backendKeyToNodes = new Map<string, string[]>(); // hub watch key → node ids
  // ADR 44 — a transformed point stays subscribed to the hub; its pipeline is
  // applied client-side to the pushed value (node id → steps).
  let pointNodeTransforms = new Map<string, Array<Record<string, unknown>>>();
  // Freshest pushed point value per node, re-applied after each render tick so
  // a (slower) poll can't clobber it.
  const livePointOverrides = new Map<string, { value: number | null; route: 'live'; ageMs: number | null }>();

  /** Distinct connector points bound in the doc; also (re)builds the
   *  point→node index used to route pushes. */
  function collectBoundPoints(): Array<{ connector_id: string; selection: Record<string, unknown> }> {
    const idx = new Map<string, string[]>();
    const descriptors = new Map<string, { connector_id: string; selection: Record<string, unknown> }>();
    const nt = new Map<string, Array<Record<string, unknown>>>();
    const walk = (n: DesignerNode): void => {
      const b = n.dataBinding as { kind?: string; connector_id?: string; selection?: Record<string, unknown>; transforms?: unknown[] } | undefined;
      if (b?.kind === 'point' && b.connector_id && b.selection) {
        const k = localPointKey(b.connector_id, b.selection);
        const arr = idx.get(k) ?? [];
        arr.push(n.id);
        idx.set(k, arr);
        if (!descriptors.has(k)) descriptors.set(k, { connector_id: b.connector_id, selection: b.selection });
        // ADR 44 — keep transformed points subscribed; the pipeline is applied to
        // the pushed value in applyPointSample (the hub delivers it raw).
        if (Array.isArray(b.transforms) && b.transforms.length) nt.set(n.id, b.transforms as Array<Record<string, unknown>>);
      }
      for (const c of n.children ?? []) walk(c);
    };
    for (const n of opts.nodes()) walk(n);
    localPointToNodes = idx;
    pointNodeTransforms = nt;
    backendKeyToNodes = new Map(); // reset; rebuilt on subscribed_points
    return [...descriptors.values()];
  }

  /** Map the hub's watch keys (from `subscribed_points`) to our node ids. */
  function bindPointKeys(subscribed: Array<{ connector_id: string; selection: Record<string, unknown>; key: string }>): void {
    const map = new Map<string, string[]>();
    for (const s of subscribed) {
      const nodes = localPointToNodes.get(localPointKey(s.connector_id, s.selection));
      if (nodes?.length) map.set(s.key, nodes);
    }
    backendKeyToNodes = map;
  }

  /** A pushed point value: feed the bound shapes DIRECTLY (no re-render → no
   *  per-viewer agent read; the hub already coalesced the read). */
  function applyPointSample(s: { key: string; value: number | null; ts: string | null; route: 'live' }): void {
    const ids = backendKeyToNodes.get(s.key);
    if (!ids?.length) return;
    const ageMs = s.ts ? Math.max(0, Date.now() - Date.parse(s.ts)) : null;
    const next = { ...effects.value };
    for (const id of ids) {
      // ADR 44 — apply this node's transform pipeline to the raw pushed value.
      const tf = pointNodeTransforms.get(id);
      const v = tf?.length ? applyScalarTransforms(s.value, tf) : s.value;
      livePointOverrides.set(id, { value: v, route: 'live', ageMs });
      next[id] = { ...(next[id] ?? {}), value: v, route: 'live', age_ms: ageMs ?? undefined };
    }
    effects.value = next;
  }

  /** Reset the event-driven runtime state (entering/leaving Run). */
  function resetRuntime(): void {
    for (const k of Object.keys(runtimeHidden)) delete runtimeHidden[k];
    for (const k of Object.keys(runtimeVariant)) delete runtimeVariant[k];
    for (const k of Object.keys(forcedState)) delete forcedState[k];
    for (const k of Object.keys(localVars)) delete localVars[k];
  }
  function clearEffects(): void { effects.value = {}; livePointOverrides.clear(); backendKeyToNodes = new Map(); }

  onBeforeUnmount(() => { stopPolling(); cancelScheduledTick(); });

  return {
    effects, liveEffects, forcedState, localVars,
    findNode, runAction, onTrigger, onControl, onPluginSetVar, onPluginCommand,
    tick, scheduleTick, cancelScheduledTick, startPolling, stopPolling, needsRender,
    collectBoundVars, applyLiveSample, collectBoundPoints, bindPointKeys, applyPointSample, resetRuntime, clearEffects,
  };
}
